import { create } from 'zustand'
import {
  getActiveCompany,
  logger,
  isReady as appContextReady,
  CompanyPreferences,
  getUserDetails,
  exceptionMessageHandler,
  appConfig,
  readMultisiteSelectedCompanies,
} from '$utils'
import { mercuryApi } from '$api'
import { IAvatarFile, ILoadedVenue } from '$models'
import { getCroppedImg, dataURLtoFile } from '$utils'
import { staffMemberService } from '$services'
import dayjs, { Dayjs } from 'dayjs'
import { RangeFilter } from '$constants'
import { ActiveCompany, ReportsMetadata, UserDetails } from '$models'
import { subscribeWithSelector } from 'zustand/middleware'
import {
  getRangeDates,
  setCompanyDayStartAndTimeZone,
} from 'src/components/common/helpers'
import { AVATAR_CACHE_CONFIG } from '$constants'
import {
  getAllVenues,
  getMultisiteEnabled,
  refreshAppContext,
} from 'src/utils/appContext'

/**
 * The selected venue ids for multisite reports. React owns this state: it reads
 * the persisted set straight from storage and materialises the all-selected
 * default (every accessible venue) when nothing is saved, matching the default
 * the AngularJS multisite service used to provide.
 */
function resolveMultisiteSelectedCompanies(venues: ILoadedVenue[]): string[] {
  const stored = readMultisiteSelectedCompanies()
  if (stored !== null) {
    return stored
  }
  return venues
    .map((venue) => venue.id)
    .filter((id): id is string => Boolean(id))
}

function initializeAppStateStore(resetReportsMetadata = true) {
  // We immediately fetch feature flags the first time this module is used. We
  // can rely upon the fact only the first import will actually execute this
  // to avoid unnecessary calls.
  logger.debug('Initialising appState store')

  appContextReady().then(() => {
    const activeCompany = getActiveCompany()

    logger.debug('Active company', activeCompany)

    const userDetails = getUserDetails()

    // A user mid-signup has no companies yet (the AngularJS bridge announces
    // the app context regardless - see app/startup/reactBridge.run.js). The
    // app state is still marked ready, with no active company, so pre-company
    // pages like Create venue can render; all the company-scoped work below
    // (feature flags, avatars, reports metadata) is skipped as there is no
    // company to scope it to.
    if (!activeCompany) {
      useAppStore.setState({
        isAppStateReady: true,
        activeCompany: null,
        userDetails: userDetails,
        venues: [],
        multisiteEnabled: false,
        multisiteSelectedCompanies: [],
      })
      return
    }

    fetchAndUpdateFeatureFlags()
    const allVenues = getAllVenues()

    useAppStore.setState({
      isAppStateReady: true,
      activeCompany: activeCompany,
      userDetails: userDetails,
      venues: allVenues,
      multisiteEnabled: getMultisiteEnabled(),
      multisiteSelectedCompanies: resolveMultisiteSelectedCompanies(allVenues),
    })

    // Fetch staff member avatars after app state is ready
    useAppStore.getState().fetchAllStaffMembersAvatars()

    // This needs to be done after the active company is set, as the company day start preference
    // is used to set the start date of the reports metadata
    const todayAtCompanyStartTime = setCompanyDayStartAndTimeZone(dayjs())

    logger.debug('Active user', userDetails)

    // This depends on the today date calculated above, so we need to do this after the active
    useAppStore.setState({
      ...(resetReportsMetadata && {
        reportsMetadata: {
          // We default to using a single day of data, starting at the beginning
          // of the current day, and ending at the beginning of the next day.
          startDateTime: todayAtCompanyStartTime,
          endDateTime: todayAtCompanyStartTime.add(1, 'day'),
          dateRange: RangeFilter.Day,
          prevDateRange: RangeFilter.Day,
        },
      }),
    })
  })
}

initializeAppStateStore()

type AppState = {
  featureFlags: Set<string>
  reportsMetadata: ReportsMetadata
  isAppStateReady: boolean
  activeCompany: ActiveCompany | null
  userDetails: UserDetails
  pageTitle: string | null
  allStaffMembersAvatars: Record<string, IAvatarFile['avatar']> | undefined
  venues: ILoadedVenue[]
  // Whether AngularJS is currently showing a multisite report (per-route).
  multisiteEnabled: boolean
  // The persisted selected venue ids for multisite reports. An empty array
  // genuinely means "nothing selected"; the all-selected default is already
  // materialised to the full venue id list by AngularJS before it reaches here.
  multisiteSelectedCompanies: string[]
}

type AppActions = {
  setFeatureFlags: (newFlags: string[]) => void
  setReportsMetadata: (
    startDateTime: Dayjs,
    endDateTime: Dayjs,
    dateRange: RangeFilter,
    prevDateRange: RangeFilter
  ) => void

  // This is used to set the default reports range when the user navigates to a page for the first time
  // and doesn't have query string params and it needs top default the date range so something differnet
  // than the current day
  setDefaultReportsRange: (date: Dayjs, range: RangeFilter) => void

  setPageTitle: (title: string | null) => void

  setUserDetails: (details: Partial<UserDetails>) => void

  fetchAllStaffMembersAvatars: () => Promise<void>

  updateStaffMemberAvatar: (
    staffMemberId: string,
    imageUrl: string,
    croppedArea: { x: number; y: number; width: number; height: number }
  ) => Promise<void>

  deleteStaffMemberAvatar: (staffMemberId: string) => Promise<void>

  reset: () => void
}

const initialState: AppState = {
  reportsMetadata: {
    startDateTime: dayjs(),
    endDateTime: dayjs(),
    dateRange: RangeFilter.Day,
    prevDateRange: RangeFilter.Day,
  },
  featureFlags: new Set<string>(),

  isAppStateReady: false,
  activeCompany: null,
  userDetails: {},
  pageTitle: null,
  allStaffMembersAvatars: undefined,
  venues: [],
  multisiteEnabled: false,
  multisiteSelectedCompanies: [],
}

/**
 * Overall app state store
 */
const useAppStore = create(
  subscribeWithSelector<AppState & AppActions>((set, get) => ({
    ...initialState,

    // actions
    setFeatureFlags: (newFlags: string[]) =>
      set(() => ({ featureFlags: new Set(newFlags) })),

    setReportsMetadata: (
      start: Dayjs,
      end: Dayjs,
      range: RangeFilter,
      prevRange: RangeFilter
    ) =>
      set({
        reportsMetadata: {
          startDateTime: start,
          endDateTime: end,
          dateRange: range,
          prevDateRange: prevRange,
        },
      }),

    setDefaultReportsRange: (date: Dayjs, range: RangeFilter) => {
      // If range is not week then we don't need to set the week start preference
      const weekStartPreference: number | undefined =
        range === RangeFilter.Week
          ? get().activeCompany?.preference(CompanyPreferences.WeekStart)
          : undefined

      date = setCompanyDayStartAndTimeZone(date)

      const { startDate, endDate } = getRangeDates(
        date,
        range,
        range,
        weekStartPreference
      )

      set(() => ({
        reportsMetadata: {
          startDateTime: startDate,
          endDateTime: endDate.add(1, 'day'),
          dateRange: range,
          prevDateRange: range,
        },
      }))
    },

    setPageTitle: (title: string | null) => set({ pageTitle: title }),

    // Merges updated details for the signed-in user (e.g. after the My Profile
    // page saves a change) so the topbar reflects them immediately. In the
    // AngularJS-hosted app the authoritative copy follows via the
    // `@@loaded/reactUserDetailsChange` -> `userInfoUpdated` round-trip below.
    setUserDetails: (details: Partial<UserDetails>) =>
      set((state) => ({
        userDetails: { ...state.userDetails, ...details },
      })),

    fetchAllStaffMembersAvatars: async () => {
      try {
        const activeCompany = get().activeCompany
        if (!activeCompany?.id) {
          logger.warn('No active company found, skipping avatar fetch')
          return
        }

        // Check if we have valid cache for this company
        if ('caches' in window) {
          try {
            const cache = await caches.open(AVATAR_CACHE_CONFIG.CACHE_NAME)
            const cacheKey = `${AVATAR_CACHE_CONFIG.AVATAR_ENDPOINT}?companyId=${activeCompany.id}`
            const cachedResponse = await cache.match(cacheKey)

            if (cachedResponse) {
              const cachedTime = cachedResponse.headers.get(
                AVATAR_CACHE_CONFIG.CACHE_TIME_HEADER
              )
              if (cachedTime) {
                const cacheAge = Date.now() - parseInt(cachedTime, 10)

                if (cacheAge < AVATAR_CACHE_CONFIG.CACHE_DURATION) {
                  // Cache is still valid, extract company avatars
                  const cachedData = await cachedResponse.json()
                  const companyAvatars = cachedData[activeCompany.id] || {}

                  logger.debug(
                    'Using cached avatars for company',
                    activeCompany.id
                  )
                  set({ allStaffMembersAvatars: companyAvatars })
                  return // Skip API call
                }
              }
            }
          } catch (cacheError) {
            logger.debug(
              'Cache check failed, proceeding with API call',
              cacheError
            )
          }
        }

        // No valid cache found, fetch from API
        logger.debug(
          'Fetching fresh avatars from API for company',
          activeCompany.id
        )
        const allStaffMembersAvatars =
          await staffMemberService.getAllStaffMemberAvatars()

        set({ allStaffMembersAvatars: allStaffMembersAvatars || {} })
      } catch (error) {
        logger.error('Failed to fetch staff member avatars', error)
        // We don't want to throw an error snackbar here as if the fetch
        // avatar fails for some reason, we can still show the avatars with
        // the staff member's initials
      }
    },

    updateStaffMemberAvatar: async (
      staffMemberId: string,
      imageUrl: string,
      croppedArea: { x: number; y: number; width: number; height: number }
    ) => {
      try {
        logger.debug('Processing avatar upload with cropping', {
          staffMemberId,
          croppedArea,
        })

        // Crop the image
        const croppedImg = await getCroppedImg(imageUrl, croppedArea)
        const file = await dataURLtoFile(croppedImg, 'cropped-avatar')

        // Upload the cropped image
        const response = await staffMemberService.updateStaffMemberAvatar(
          file,
          staffMemberId
        )

        if (response.avatar) {
          // Update store and service worker cache with the new avatar
          set((state) => ({
            allStaffMembersAvatars: {
              ...state.allStaffMembersAvatars,
              [staffMemberId]: response.avatar,
            },
          }))

          // Update service worker cache
          if (
            'serviceWorker' in navigator &&
            navigator.serviceWorker.controller
          ) {
            const activeCompany = get().activeCompany
            if (activeCompany?.id) {
              navigator.serviceWorker.controller.postMessage({
                type: 'UPDATE_AVATAR_CACHE',
                staffMemberId: staffMemberId,
                avatar: response.avatar,
                companyId: activeCompany.id,
              })
            }
          }
          logger.debug('Avatar upload completed successfully', {
            staffMemberId,
          })
        }
      } catch (error) {
        logger.error('Failed to upload and crop staff member avatar:', error)
        exceptionMessageHandler(error, 'Unable to update avatar')
        throw error
      }
    },

    deleteStaffMemberAvatar: async (staffMemberId: string) => {
      try {
        logger.debug('Deleting staff member avatar', { staffMemberId })

        // Call the delete API
        await staffMemberService.deleteStaffMemberAvatar(staffMemberId)

        // Remove from store
        set((state) => {
          const updatedAvatars = { ...state.allStaffMembersAvatars }
          delete updatedAvatars[staffMemberId]
          return { allStaffMembersAvatars: updatedAvatars }
        })

        // Update service worker cache
        if (
          'serviceWorker' in navigator &&
          navigator.serviceWorker.controller
        ) {
          const activeCompany = get().activeCompany
          if (activeCompany?.id) {
            navigator.serviceWorker.controller.postMessage({
              type: 'DELETE_AVATAR_CACHE',
              staffMemberId: staffMemberId,
              companyId: activeCompany.id,
            })
          }
        }

        logger.debug('Avatar deleted successfully', { staffMemberId })
      } catch (error) {
        logger.error('Failed to delete staff member avatar:', error)
        exceptionMessageHandler(error, 'Unable to delete avatar')
        throw error
      }
    },

    reset: () => set(initialState),
  }))
)

export default useAppStore

// Listen for notification of context changes and update any store data that is
// context dependent.
window.addEventListener('@@loaded/appContextChange', (ev: Event) => {
  const { type } = (ev as CustomEvent).detail
  logger.debug(
    'AppState store received notification the app context %s data has changed',
    type
  )

  if (type === 'user-info') {
    // Multisite mode (whether an AngularJS multisite report is open) rides on
    // `userInfoUpdated` broadcasts, so refresh it here. We re-read the bridge's
    // appContext first (rather than racing the change listener in appContext.ts)
    // so the store reflects the latest mode. The selected venue set is owned by
    // React, so we re-resolve it straight from storage. User details ride on the
    // same broadcast (e.g. `userinfo.setUserDetails` after a profile change), so
    // refresh those too.
    refreshAppContext().then(() => {
      useAppStore.setState({
        userDetails: getUserDetails(),
        multisiteEnabled: getMultisiteEnabled(),
        multisiteSelectedCompanies: resolveMultisiteSelectedCompanies(
          useAppStore.getState().venues
        ),
      })
    })
  }

  if (type === 'company') {
    /* We need to initialize the app state with the new active company
    but we want to keep the reports metadata between companies.

    Refresh the bridge's app context first (as the 'user-info' branch does)
    rather than relying on the separate listener in appContext.ts having already
    updated CurrentAppContext.data - listener invocation order on `window` is
    registration-order dependent, and initializeAppStateStore reads that cache,
    so refreshing here makes this branch read fresh company data on its own. */
    refreshAppContext().then(() => {
      initializeAppStateStore(false)
    })
  }
})

// Helper function to actually get feature flags from the API and update the
// store. When components read from the store they should get the most up to
// date flags.
function fetchAndUpdateFeatureFlags() {
  mercuryApi
    .get<{ [key: string]: boolean }>('/features')
    .then((featureFlagData) => {
      // Build an array of the names of each feature flag that is set to `true`
      // in the API response.
      const flags = Object.keys(featureFlagData).filter(
        (flag) => featureFlagData[flag]
      )

      logger.debug('Active feature flags are %o', flags)

      // and update the store, this will trigger a re-render of any components
      // that read feature flags from the store.
      useAppStore.getState().setFeatureFlags(flags)
    })
    .catch((error) => {
      logger.error('Unable to read feature flags %s due to %o', error)
      exceptionMessageHandler(error, 'Unable to read feature flags')
    })
}

/* The purpose of this function is to listen to the changes on the 
AppState store isAppStateReady property so the page loaders can await on the
AppState to update it and be able to read the active company and reports metadata */
export async function isAppStateReady() {
  return new Promise<void>((resolve) => {
    //This standalone value is set in react-app/index.html, which is the entry point
    //for standalone pages
    const isStandalone = appConfig.get<boolean>('standalone') ?? false

    if (isStandalone) {
      logger.warn(
        'Running in standalone mode, not waiting for AngularJS app state'
      )
      resolve()
    }

    if (useAppStore.getState().isAppStateReady) {
      logger.debug('AppState is already ready')
      resolve()
    }

    const unsubscribe = useAppStore.subscribe(
      (state) => state.isAppStateReady,
      (isNowReady) => {
        logger.debug(
          'appStateAwaiter promise heard state change to',
          isNowReady
        )
        if (isNowReady) {
          unsubscribe()
          resolve()
        }
      }
    )
  })
}
